import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthRedirect } from '../utils/authRedirect';
import ConfirmationModal from './ConfirmationModal';

function SavedRecipes() {
    const [recipes, setRecipes] = useState([]);
    const searchRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [allTags, setAllTags] = useState([]);
    const [selectedTag, setSelectedTag] = useState(null);
    const [quickFilter, setQuickFilter] = useState('all');
    const [viewMode, setViewMode] = useState('list');
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [recipeToDelete, setRecipeToDelete] = useState(null);

    const handleAuthError = useAuthRedirect();

    useEffect(() => {
        if (searchRef && searchRef.current) searchRef.current.focus();

        const fetchRecipesAndTags = async () => {
            setLoading(true);
            setError('');
            try {
                const token = localStorage.getItem('token');
                const recipeResponse = await fetch('/api/recipes', {
                    credentials: 'same-origin',
                    headers: { 'x-auth-token': token },
                });

                if (handleAuthError(recipeResponse.status)) {
                    return;
                }

                if (!recipeResponse.ok) {
                    const errData = await recipeResponse.json();
                    throw new Error(errData.error || 'Something went wrong while fetching recipes.');
                }

                const recipeData = await recipeResponse.json();
                setRecipes(recipeData);

                const tagsResponse = await fetch('/api/recipes/tags', {
                    credentials: 'same-origin',
                    headers: { 'x-auth-token': token },
                });

                if (handleAuthError(tagsResponse.status)) {
                    return;
                }

                if (!tagsResponse.ok) {
                    const errData = await tagsResponse.json();
                    throw new Error(errData.error || 'Something went wrong while fetching tags.');
                }

                const tagsData = await tagsResponse.json();
                setAllTags(tagsData);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchRecipesAndTags();
    }, [handleAuthError]);

    const confirmDelete = (id) => {
        setRecipeToDelete(id);
        setShowModal(true);
    };

    const cancelDelete = () => {
        setShowModal(false);
        setRecipeToDelete(null);
    };

    const executeDelete = async () => {
        setShowModal(false);
        if (!recipeToDelete) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/recipes/${recipeToDelete}`, {
                method: 'DELETE',
                credentials: 'same-origin',
                headers: { 'x-auth-token': token },
            });

            if (handleAuthError(response.status)) {
                return;
            }

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Something went wrong while deleting the recipe.');
            }

            setRecipes((previousRecipes) => previousRecipes.filter((recipe) => recipe._id !== recipeToDelete));

            const tokenAfterDelete = localStorage.getItem('token');
            const tagsResponse = await fetch('/api/recipes/tags', {
                credentials: 'same-origin',
                headers: { 'x-auth-token': tokenAfterDelete },
            });
            if (tagsResponse.ok) {
                const tagsData = await tagsResponse.json();
                setAllTags(tagsData);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setRecipeToDelete(null);
        }
    };

    const sortedRecipes = useMemo(() => (
        [...recipes].sort((a, b) => {
            const aTime = new Date(a.createdAt || 0).getTime();
            const bTime = new Date(b.createdAt || 0).getTime();
            return bTime - aTime;
        })
    ), [recipes]);

    const recentRecipeIds = useMemo(() => (
        new Set(sortedRecipes.slice(0, 12).map((recipe) => recipe._id))
    ), [sortedRecipes]);

    const visibleRecipes = useMemo(() => (
        sortedRecipes.filter((recipe) => {
            const normalizedSearch = searchTerm.trim().toLowerCase();
            const recipeTitle = (recipe.title || '').toLowerCase();
            const recipeTags = Array.isArray(recipe.tags) ? recipe.tags : [];
            const matchesSearch = !normalizedSearch
                || recipeTitle.includes(normalizedSearch)
                || recipeTags.some((tag) => tag.toLowerCase().includes(normalizedSearch));

            const matchesFilter = quickFilter === 'all'
                || (quickFilter === 'recent' && recentRecipeIds.has(recipe._id))
                || (quickFilter === 'tag' && selectedTag && recipeTags.includes(selectedTag));

            return matchesSearch && matchesFilter;
        })
    ), [sortedRecipes, searchTerm, quickFilter, selectedTag, recentRecipeIds]);

    const activeFilterLabel = quickFilter === 'all'
        ? 'All recipes'
        : quickFilter === 'recent'
            ? 'Recent recipes'
            : selectedTag
                ? `Tag: ${selectedTag}`
                : 'Tag filter';

    const clearFilters = () => {
        setSearchTerm('');
        setQuickFilter('all');
        setSelectedTag(null);
    };

    const selectTagFilter = (tag) => {
        setSelectedTag(tag);
        setQuickFilter('tag');
    };

    const toSafeExternalUrl = (value) => {
        if (typeof value !== 'string') return '';
        try {
            const parsed = new URL(value);
            return ['http:', 'https:'].includes(parsed.protocol) ? parsed.toString() : '';
        } catch (err) {
            return '';
        }
    };

    return (
        <div className="page saved-recipes-page">
            <header className="page-header">
                <span className="page-eyebrow">Saved recipes</span>
                <h1>Your cookbook</h1>
                <p className="page-subtitle">
                    A working library for real cooking days: search quickly, open, and get moving.
                </p>
            </header>

            <section className="saved-recipes-toolbar" aria-label="Recipe search and filters">
                <div className="saved-recipes-toolbar__header">
                    <h2>Find a recipe quickly</h2>
                    <p className="text-muted mb-0">Start with search, then narrow by recency or tag.</p>
                </div>

                <div className="saved-recipes-view-toggle" role="group" aria-label="Recipe layout mode">
                    <button
                        type="button"
                        className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'} saved-recipes-filter-btn`}
                        onClick={() => setViewMode('list')}
                    >
                        List view
                    </button>
                    <button
                        type="button"
                        className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'} saved-recipes-filter-btn`}
                        onClick={() => setViewMode('grid')}
                    >
                        Grid view
                    </button>
                </div>

                <label htmlFor="saved-recipes-search" className="form-label mb-2">Search saved recipes</label>
                <input
                    id="saved-recipes-search"
                    ref={searchRef}
                    aria-label="Search saved recipes"
                    type="text"
                    className="form-control saved-recipes-search-input"
                    placeholder="Search title or tag"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />

                <div className="saved-recipes-quick-filters" role="group" aria-label="Quick filters">
                    <button
                        type="button"
                        className={`btn ${quickFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'} saved-recipes-filter-btn`}
                        onClick={() => {
                            setQuickFilter('all');
                            setSelectedTag(null);
                        }}
                    >
                        All
                    </button>
                    <button
                        type="button"
                        className={`btn ${quickFilter === 'recent' ? 'btn-primary' : 'btn-outline-secondary'} saved-recipes-filter-btn`}
                        onClick={() => {
                            setQuickFilter('recent');
                            setSelectedTag(null);
                        }}
                    >
                        Recent
                    </button>
                    <button
                        type="button"
                        className={`btn ${quickFilter === 'tag' ? 'btn-primary' : 'btn-outline-secondary'} saved-recipes-filter-btn`}
                        onClick={() => {
                            setQuickFilter('tag');
                            if (!selectedTag && allTags[0]) {
                                setSelectedTag(allTags[0]);
                            }
                        }}
                        disabled={allTags.length === 0}
                    >
                        By tag
                    </button>
                </div>

                {quickFilter === 'tag' && (
                    <div className="saved-recipes-tags" role="group" aria-label="Filter by tag">
                        {allTags.map((tag) => (
                            <button
                                type="button"
                                key={tag}
                                className={`btn ${selectedTag === tag ? 'btn-primary' : 'btn-outline-secondary'} saved-recipes-tag-btn`}
                                onClick={() => selectTagFilter(tag)}
                            >
                                {tag}
                            </button>
                        ))}
                    </div>
                )}

                <div className="saved-recipes-toolbar__footer">
                    <p className="mb-0">
                        <strong>{visibleRecipes.length}</strong> results · <span>{activeFilterLabel}</span>
                    </p>
                    {(searchTerm || quickFilter !== 'all') && (
                        <button
                            type="button"
                            className="btn btn-link"
                            aria-label="Clear search and filters"
                            onClick={clearFilters}
                        >
                            Clear filters
                        </button>
                    )}
                </div>
                <div aria-live="polite" className="sr-only">{visibleRecipes.length} results</div>
            </section>

            {loading ? (
                <section className="empty-state saved-recipes-state" aria-live="polite">
                    <h2>Loading your recipes…</h2>
                    <p>We are organizing your cookbook now.</p>
                </section>
            ) : error ? (
                <section className="empty-state saved-recipes-state" role="alert">
                    <h2>Could not load saved recipes</h2>
                    <p>{error}</p>
                    <button type="button" className="btn btn-secondary" onClick={() => window.location.reload()}>
                        Try again
                    </button>
                </section>
            ) : (
                <section className="profile-recipes mt-4" aria-label="Saved recipes list">
                    {visibleRecipes.length === 0 ? (
                        <div className="empty-state saved-recipes-state">
                            <h2>No recipes found</h2>
                            <p>
                                {searchTerm || quickFilter !== 'all'
                                    ? 'Try another search or clear filters to see all saved recipes.'
                                    : 'You have not saved any recipes yet.'}
                            </p>
                            {!searchTerm && quickFilter === 'all' && (
                                <Link to="/app" className="btn btn-primary">
                                    Open extractor
                                </Link>
                            )}
                        </div>
                    ) : (
                        <div className={`saved-recipes-list ${viewMode === 'grid' ? 'is-grid' : 'is-list'}`}>
                            {visibleRecipes.map((recipe) => (
                                (() => {
                                    const safeUrl = toSafeExternalUrl(recipe.url);
                                    return (
                                <article key={recipe._id} className={`card recipe-card saved-recipe-row ${viewMode === 'grid' ? 'saved-recipe-row--grid' : ''}`}>
                                    <div className="card-body saved-recipe-row__body">
                                        <div className="saved-recipe-row__content">
                                            <h3 className="saved-recipe-row__title">{recipe.title}</h3>
                                            <div className="saved-recipe-row__meta">
                                                {recipe.createdAt && (
                                                    <span>
                                                        Saved {new Date(recipe.createdAt).toLocaleDateString()}
                                                    </span>
                                                )}
                                                <span>
                                                    {Array.isArray(recipe.notes) ? recipe.notes.length : 0} note{Array.isArray(recipe.notes) && recipe.notes.length === 1 ? '' : 's'}
                                                </span>
                                                {recipe.tags && recipe.tags.length > 0 && (
                                                    <div className="saved-recipe-meta">
                                                        {recipe.tags.map((tag) => (
                                                            <span key={`${recipe._id}-${tag}`} className="tag-pill">{tag}</span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="saved-recipe-row__actions">
                                            <Link to={`/recipes/${recipe._id}`} className="btn btn-primary saved-recipe-row__primary">
                                                Open recipe
                                            </Link>
                                            {safeUrl && (
                                                <a
                                                    href={safeUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-secondary"
                                                >
                                                    Source
                                                </a>
                                            )}
                                            <button
                                                onClick={() => confirmDelete(recipe._id)}
                                                aria-label={`Delete recipe ${recipe.title}`}
                                                className="btn btn-danger"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </article>
                                    );
                                })()
                            ))}
                        </div>
                    )}
                </section>
            )}

            <ConfirmationModal
                show={showModal}
                title="Confirm Deletion"
                message="Are you sure you want to delete this recipe? This action cannot be undone."
                onConfirm={executeDelete}
                onCancel={cancelDelete}
            />
        </div>
    );
}

export default SavedRecipes;
